import React, { FunctionComponent, useEffect, useState } from "react";
import { DataField } from "@iventis/domain-model/model/dataField";
import { DataFieldListItem } from "@iventis/domain-model/model/dataFieldListItem";
import { DataFieldType } from "@iventis/domain-model/model/dataFieldType";
import { Box, Checkbox, TextField, useTheme } from "@mui/material";
import { Content } from "@iventis/translations/content/typed-content";
import { DataFieldValueTypes } from "@iventis/types";
import { AutocompleteWithLoading, renderMultiSelectLabel, allOptionsValueLiteral, ConciseSelector } from "@iventis/components";
import { StyledFieldLabel } from "@iventis/styles/src/components/forms";
import { styled } from "@iventis/styles";
import { Theme } from "@emotion/react";
import { borderRadius, labelMargin, fontSizes } from "@iventis/styles/src/atomic-rules";
import { CheckedCheckBox, UncheckedCheckBox } from "@iventis/styles/src/components/checkbox-elements";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { FilterFormat } from "@iventis/types/api.types";
import { getDataFieldListItems, titleFormat } from "@iventis/utilities";
import { scheduleApi } from "@iventis/api/src/api";
import { EventBlockValues } from "../schedule.types";
import { getDataFieldListItemDisplayTextWithCode } from "../data-field-helpers";
import { getSessionBoxColours, sessionDefaultColour } from "../event-block-helpers";

/* eslint-disable @typescript-eslint/no-explicit-any */
/** Recursive tree matching the parent forms schema with the bottom level propertys pointing to booleans representing the touched status of the property's input */
type InputTouched<Values> = {
    [K in keyof Values]?: Values[K] extends any[]
        ? Values[K][number] extends object
            ? InputTouched<Values[K][number]>[]
            : boolean
        : Values[K] extends object
        ? InputTouched<Values[K]>
        : boolean;
};

/** Recursive tree matching the parent forms schema with the bottom level propertys pointing to booleans representing the error status of the property's input */
type InputErrors<Values> = {
    [K in keyof Values]?: Values[K] extends any[]
        ? Values[K][number] extends object
            ? InputErrors<Values[K][number]>[] | string | string[]
            : string | string[]
        : Values[K] extends object
        ? InputErrors<Values[K]>
        : string;
};

interface DataFieldInputProps {
    dataField: DataField;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
    handleBlur?: {
        /** Classic React blur handler, keyed by input name */
        (e: React.FocusEvent<any>): void;
        /** Preact-like linkState. Will return a handleBlur function. */
        <T = string | any>(fieldOrEvent: T): T extends string ? (e: any) => void : void;
    };
    handleChange: {
        /** Classic React change handler, keyed by input name */
        (e: React.ChangeEvent<any>): void;
        /** Preact-like linkState. Will return a handleChange function.  */
        <T = string | React.ChangeEvent<any>>(field: T): T extends React.ChangeEvent<any> ? void : (e: string | React.ChangeEvent<any>) => void;
    };
    touched?: InputTouched<EventBlockValues>;
    errors?: InputErrors<EventBlockValues>;
    values: { [name: string]: DataFieldValueTypes };
    label?: string;
    filter?: FilterFormat[];
    disabled?: boolean;
    autocompleteWithLoadingClassName?: string;
    onListItemValueChange?: () => void;
    renderOption?: (props, option) => React.ReactNode;
    displayCode?: boolean;
    renderAdornment?: (listItem: DataFieldListItem) => React.ReactNode;
    /* eslint-enable @typescript-eslint/no-explicit-any */
}

const DataFieldInputComponent: FunctionComponent<DataFieldInputProps> = ({
    dataField,
    setFieldValue,
    handleBlur,
    handleChange,
    touched,
    errors,
    values,
    label,
    filter,
    disabled = false,
    autocompleteWithLoadingClassName,
    onListItemValueChange: onUserChangingValue,
    renderOption,
    displayCode,
    renderAdornment,
}) => {
    const value = values[dataField.id];
    const translate = useIventisTranslate();
    const name = translate(Content.schedule2.data_fields[dataField.name]) || dataField.name;
    const [dataFieldListItems, setDataFieldListItems] = useState<DataFieldListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const theme = useTheme<Theme>();

    useEffect(() => {
        if ((dataField.type === DataFieldType.List || dataField.type === DataFieldType.MultiSelect) && !disabled) {
            setLoading(true);
            getDataFieldListItems(scheduleApi, dataField.id, filter).then((listItems) => {
                setDataFieldListItems(listItems);
                setLoading(false);
            });
        }
    }, [JSON.stringify(filter), disabled]);

    switch (dataField.type) {
        case DataFieldType.List: {
            return (
                <StyledAutocompleteWithLoading
                    value={value || null}
                    label={label || name}
                    placeholder={translate(Content.common.buttons.select)}
                    options={dataFieldListItems}
                    onChange={(value) => {
                        onUserChangingValue?.();
                        setFieldValue(dataField.id, value);
                    }}
                    getOptionLabel={(option: DataFieldListItem) => titleFormat(displayCode ? getDataFieldListItemDisplayTextWithCode(option, dataField) : option.name) || "Select"}
                    getOptionSelected={(option: DataFieldListItem, value: DataFieldListItem) => {
                        const compValue = value ? value.id : "";
                        return option.id === compValue;
                    }}
                    onBlur={handleBlur}
                    error={touched?.dataFieldValues?.[dataField.id] && errors?.dataFieldValues?.[dataField.id] !== undefined}
                    helperText={touched?.dataFieldValues?.[dataField.id] && errors?.dataFieldValues?.[dataField.id]}
                    loadingOptions={loading}
                    disabled={disabled}
                    className={autocompleteWithLoadingClassName}
                    renderOption={renderOption || null}
                    renderAdornment={renderAdornment || null}
                />
            );
        }
        case DataFieldType.MultiSelect: {
            const checkedItems = (value || []) as DataFieldListItem[];
            return (
                <StyledMultiSelect>
                    <StyledFieldLabel>
                        {Content.schedule2.data_fields[dataField.name] ? <span>{translate(Content.schedule2.data_fields[dataField.name])}</span> : <span>{dataField.name}</span>}
                    </StyledFieldLabel>
                    <ConciseSelector
                        multi
                        renderLabel={() => renderMultiSelectLabel(checkedItems, { translate, valueGetter: (option) => option.metaData?.code })}
                        options={dataFieldListItems.map(({ id, name }) => ({ id, label: name })).sort((a, b) => a.label.localeCompare(b.label))}
                        values={checkedItems?.map((selected) => selected.id) || []}
                        onChange={(selection) => {
                            onUserChangingValue?.();
                            if (selection.includes(allOptionsValueLiteral)) {
                                setFieldValue(dataField.id, dataFieldListItems.length === checkedItems.length ? [] : dataFieldListItems);
                            } else {
                                setFieldValue(
                                    dataField.id,
                                    dataFieldListItems.filter((option) => selection.includes(option.id))
                                );
                            }
                        }}
                        toggleAllOptions
                        toggleAllOptionsLabel={translate(Content.common.buttons.select_all)}
                        disabled={disabled}
                    />
                </StyledMultiSelect>
            );
        }
        case DataFieldType.Text: {
            return (
                <StyledTextInput>
                    <StyledFieldLabel>
                        {Content.schedule2.data_fields[dataField.name] ? <span>{translate(Content.schedule2.data_fields[dataField.name])}</span> : <span>{dataField.name}</span>}
                    </StyledFieldLabel>
                    <TextField
                        type="text"
                        className="dataField-text-input"
                        name={`dataFieldValues.${dataField.id}`}
                        variant="outlined"
                        value={value}
                        onChange={(e) => setFieldValue(dataField.id, e.target.value)}
                        error={!!(touched?.dataFieldValues?.[dataField.id] && errors?.dataFieldValues?.[dataField.id])}
                        helperText={touched?.dataFieldValues?.[dataField.id] && errors?.dataFieldValues?.[dataField.id]}
                        onBlur={handleBlur}
                    />
                </StyledTextInput>
            );
        }
        case DataFieldType.Number: {
            return (
                <TextField
                    type="number"
                    name={`dataFieldValues.${dataField.id}`}
                    variant="outlined"
                    value={value}
                    onChange={handleChange}
                    error={!!(touched?.dataFieldValues?.[dataField.id] && errors?.dataFieldValues?.[dataField.id])}
                    helperText={touched?.dataFieldValues?.[dataField.id] && errors?.dataFieldValues?.[dataField.id]}
                    onBlur={handleBlur}
                />
            );
        }
        case DataFieldType.Tickbox: {
            return (
                <StyledTickBoxInput>
                    <StyledFieldLabel>
                        {Content.schedule2.data_fields[dataField.name] ? <span>{translate(Content.schedule2.data_fields[dataField.name])}</span> : <span>{dataField.name}</span>}
                    </StyledFieldLabel>
                    <Checkbox
                        checked={value as boolean}
                        icon={<UncheckedCheckBox size={15} borderStyle={`1px solid ${theme.typographyColors.subdued}`} backgroundColour={theme.secondaryColors.blank} />}
                        checkedIcon={
                            <CheckedCheckBox
                                size={15}
                                tickColour={theme.shades.two}
                                borderStyle={`1px solid ${theme.typographyColors.subdued}`}
                                backgroundColour={theme.secondaryColors.blank}
                            />
                        }
                        color="default"
                        onChange={(e) => setFieldValue(dataField.id, e.target.checked)}
                        style={{ padding: "0px", alignSelf: "start" }}
                    />
                </StyledTickBoxInput>
            );
        }
        default:
            return null;
    }
};

const StyledTickBoxInput = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: ${labelMargin};
`;

const StyledTextInput = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    .dataField-text-input {
        width: 100%;
    }
`;

const StyledMultiSelect = styled.div`
    // TODO: Switch this to global mui theme on all selects during the styling refactor.
    .MuiInputBase-root {
        border-radius: ${borderRadius.input};
        border: 1px solid ${({ theme }: { theme: Theme }) => theme.shades.one};
        color: ${({ theme }: { theme: Theme }) => theme.typographyColors.core};
    }
    .MuiFormControl-root {
        width: 100%;
    }
    .MuiSelect-outlined {
        width: 100%;
        position: relative;
    }
`;

const StyledAutocompleteWithLoading = styled(AutocompleteWithLoading)`
    flex-grow: 1;
`;

export const renderOptionWithBox = (props: React.HTMLAttributes<HTMLLIElement>, option: DataFieldListItem) => (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Box style={{ fontSize: `${fontSizes.small}` }} component="li" sx={{ "& > Box": { mr: 2, flexShrink: 0 } }} {...props}>
        {renderMedalBox(option?.metaData?.colour)}
        {option.name.replace("Competition -", "")}
    </Box>
);

export const renderMedalBox = (colour: string) => {
    const boxColour = colour ? getSessionBoxColours(colour?.trim()) : sessionDefaultColour;
    return (
        <Box
            sx={{
                width: 20,
                height: 20,
                backgroundColor: boxColour.fill,
                border: 1,
                borderWidth: 3,
                borderColor: boxColour.outline,
                borderRadius: "20%",
                marginRight: "10px",
            }}
        />
    );
};

export const DataFieldInput = React.memo(DataFieldInputComponent);
